import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { HttpException } from '../../exceptions/index';
import { ApiService } from '../../services/index';
import { VaultFolder, VaultOrder, VaultSortBy, VaultTab } from '../../types/vault.types';
import { useAppQuery } from '../use-app-query.hook';

export type GetRepositoryResponse = {
  data: VaultFolder[];
  totalPages: number;
};

export const useRepositoryFolders = ({
  companyId,
  numberOfFetchedItems = 100,
  currentPage = 1,
  search,
  sortBy,
  orderBy,
  mode,
  ...searchParams
}: {
  companyId: string;
  currentPage?: number;
  mode?: VaultTab;
  numberOfFetchedItems?: number;
  orderBy?: VaultOrder;
  search: string;
  sortBy?: VaultSortBy;
}) => {
  const queryClient = useQueryClient();
  const skip = (currentPage - 1 <= 0 ? 0 : currentPage - 1) * numberOfFetchedItems;
  const {
    data: { data = [], totalPages } = {},
    isError,
    isLoading,
    refetch,
  } = useAppQuery<GetRepositoryResponse, HttpException>({
    queryKey: [
      QueryKey.GET_HARDCODED_REPOSITORY,
      companyId,
      {
        page: currentPage,
        skip,
        numberOfFetchedItems,
        sortBy,
        orderBy,
        mode,
      },
    ],
    queryFn: () =>
      ApiService.get(
        { endpoint: BackendRoute.COMPANIES, routePath: [companyId, 'vault', 'folders'] },
        {
          queryParams: {
            ...searchParams,
            take: numberOfFetchedItems,
            skip,
            mode,
            search,
            ...(search ? { parentFolderNull: false } : {}),
            ...(orderBy && sortBy ? { sortBy, orderBy } : {}),
          },
        },
      ),
    enabled: !!companyId,
  });

  const invalidateQuery = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_HARDCODED_REPOSITORY] });
  }, [queryClient]);

  return {
    invalidateQuery,
    folders: data,
    totalPages,
    isError,
    isLoading,
    refetch,
  };
};
